import React, { useState, useEffect } from 'react';
import { 
  FileText, Layout, Navigation, Image, Database, RefreshCw, 
  Eye, Edit, Trash2, Plus, Save, X, ChevronRight, ChevronDown,
  Check, AlertCircle, Clock, History, Upload, Link as LinkIcon, FormInput
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cmsApi } from '../../lib/api';
import { toast } from 'sonner';
import FormBuilder from './FormBuilder';
import AuditLogViewer from './AuditLogViewer';

// ==================== TABS ====================
const CMS_TABS = [
  { id: 'pages', label: 'Pages', icon: FileText },
  { id: 'navigation', label: 'Navigation', icon: Navigation },
  { id: 'forms', label: 'Forms', icon: FormInput },
  { id: 'media', label: 'Media Library', icon: Image },
  { id: 'audit', label: 'Audit Log', icon: History },
  { id: 'migration', label: 'Migration', icon: Database },
];

// ==================== MAIN COMPONENT ====================
const CMSManager = () => {
  const [activeTab, setActiveTab] = useState('migration');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [pages, setPages] = useState([]);
  const [navigation, setNavigation] = useState([]);
  const [media, setMedia] = useState([]);
  
  // Editing states
  const [selectedPage, setSelectedPage] = useState(null);
  const [pageBlocks, setPageBlocks] = useState([]);
  const [editingBlock, setEditingBlock] = useState(null);
  const [pageVersions, setPageVersions] = useState([]);
  const [showSeoSettings, setShowSeoSettings] = useState(false);
  const [seoForm, setSeoForm] = useState({
    seo_title: '',
    seo_description: '',
    og_title: '',
    og_description: '',
    og_image: '',
  });
  const [savingSeo, setSavingSeo] = useState(false);
  
  // Create page state
  const [showCreatePage, setShowCreatePage] = useState(false);
  const [newPageForm, setNewPageForm] = useState({
    title: '',
    title_es: '',
    slug: '',
    template: 'default',
  });
  const [creatingPage, setCreatingPage] = useState(false);
  
  // Create block state
  const [showCreateBlock, setShowCreateBlock] = useState(false);
  const [newBlockForm, setNewBlockForm] = useState({
    block_key: '',
    block_type: 'content',
    content_json: {},
  });
  const [creatingBlock, setCreatingBlock] = useState(false);
  
  // Migration state
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [migrating, setMigrating] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [pagesRes, navRes, mediaRes] = await Promise.all([
        cmsApi.getPages().catch(() => ({ data: [] })),
        cmsApi.getNavigation().catch(() => ({ data: [] })),
        cmsApi.getMedia().catch(() => ({ data: [] })),
      ]);
      setPages(pagesRes.data || []);
      setNavigation(navRes.data || []);
      setMedia(mediaRes.data || []);
      
      // Check if content has been seeded
      if ((pagesRes.data || []).length > 0) {
        setMigrationStatus('completed');
      }
    } catch (error) {
      console.error('Error loading CMS data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPageDetails = async (pageId) => {
    try {
      const [pageRes, blocksRes, versionsRes] = await Promise.all([
        cmsApi.getPage(pageId),
        cmsApi.getPageBlocks(pageId),
        cmsApi.getPageVersions(pageId),
      ]);
      setSelectedPage(pageRes.data);
      setPageBlocks(blocksRes.data || []);
      setPageVersions(versionsRes.data || []);
      // Initialize SEO form with page data
      setSeoForm({
        seo_title: pageRes.data.seo_title || '',
        seo_description: pageRes.data.seo_description || '',
        og_title: pageRes.data.og_title || '',
        og_description: pageRes.data.og_description || '',
        og_image: pageRes.data.og_image || '',
      });
    } catch (error) {
      toast.error('Failed to load page details');
    }
  };

  // Save SEO settings for a page
  const saveSeoSettings = async () => {
    if (!selectedPage) return;
    setSavingSeo(true);
    try {
      await cmsApi.updatePage(selectedPage.id, seoForm);
      toast.success('SEO settings saved');
      // Update selected page with new SEO values
      setSelectedPage(prev => ({ ...prev, ...seoForm }));
    } catch (error) {
      toast.error('Failed to save SEO settings');
    } finally {
      setSavingSeo(false);
    }
  };

  // ==================== MIGRATION FUNCTIONS ====================
  const runContentMigration = async () => {
    if (!window.confirm('This will seed the database with your existing website content. Existing CMS content will NOT be overwritten. Continue?')) {
      return;
    }
    
    setMigrating(true);
    try {
      const response = await cmsApi.seedContent();
      setMigrationStatus('completed');
      toast.success(`Content migrated successfully! ${response.data.seeded?.pages || 0} pages, ${response.data.seeded?.blocks || 0} blocks created.`);
      loadInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Migration failed');
      setMigrationStatus('failed');
    } finally {
      setMigrating(false);
    }
  };

  // ==================== PAGE FUNCTIONS ====================
  const createPage = async () => {
    if (!newPageForm.title || !newPageForm.slug) {
      toast.error('Title and slug are required');
      return;
    }
    
    // Validate slug format
    const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    if (!slugRegex.test(newPageForm.slug)) {
      toast.error('Slug must be lowercase letters, numbers, and hyphens only');
      return;
    }
    
    setCreatingPage(true);
    try {
      await cmsApi.createPage(newPageForm);
      toast.success('Page created successfully');
      setShowCreatePage(false);
      setNewPageForm({ title: '', title_es: '', slug: '', template: 'default' });
      loadInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create page');
    } finally {
      setCreatingPage(false);
    }
  };
  
  const deletePage = async (pageId, pageSlug) => {
    const corePag = ["home", "about", "services", "pricing", "contact", "resources"];
    if (corePag.includes(pageSlug)) {
      toast.error('Cannot delete core website pages');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this page? This action cannot be undone.')) {
      return;
    }
    
    try {
      await cmsApi.deletePage(pageId);
      toast.success('Page deleted successfully');
      setSelectedPage(null);
      loadInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete page');
    }
  };

  const publishPage = async (pageId) => {
    try {
      await cmsApi.publishPage(pageId);
      toast.success('Page published successfully');
      loadInitialData();
      if (selectedPage?.id === pageId) {
        loadPageDetails(pageId);
      }
    } catch (error) {
      toast.error('Failed to publish page');
    }
  };

  const restoreVersion = async (versionId) => {
    if (!window.confirm('Restore this version? Current changes will be replaced.')) return;
    try {
      await cmsApi.restorePageVersion(selectedPage.id, versionId);
      toast.success('Version restored');
      loadPageDetails(selectedPage.id);
    } catch (error) {
      toast.error('Failed to restore version');
    }
  };

  // ==================== BLOCK FUNCTIONS ====================
  const createBlock = async () => {
    if (!newBlockForm.block_key) {
      toast.error('Block key is required');
      return;
    }
    
    setCreatingBlock(true);
    try {
      // Create default content based on block type
      let defaultContent = {};
      switch (newBlockForm.block_type) {
        case 'hero':
          defaultContent = {
            headline: 'New Hero Section',
            headline_es: 'Nueva Sección Hero',
            subheadline: 'Add your subheadline here',
            subheadline_es: 'Agregue su subtítulo aquí',
            cta_text: 'Learn More',
            cta_text_es: 'Saber Más',
            cta_url: '/contact',
            image: '',
          };
          break;
        case 'features':
          defaultContent = {
            title: 'Features Section',
            title_es: 'Sección de Características',
            features: [],
            features_es: [],
          };
          break;
        case 'cta':
          defaultContent = {
            headline: 'Call to Action',
            headline_es: 'Llamada a la Acción',
            description: 'Add your CTA description here',
            description_es: 'Agregue su descripción de CTA aquí',
            button_text: 'Get Started',
            button_text_es: 'Comenzar',
            button_url: '/contact',
          };
          break;
        case 'testimonials':
          defaultContent = {
            title: 'What Our Clients Say',
            title_es: 'Lo Que Dicen Nuestros Clientes',
            testimonials: [],
          };
          break;
        case 'content':
        default:
          defaultContent = {
            title: 'New Section',
            title_es: 'Nueva Sección',
            content: 'Add your content here...',
            content_es: 'Agregue su contenido aquí...',
          };
      }
      
      await cmsApi.createBlock(selectedPage.id, {
        block_key: newBlockForm.block_key,
        block_type: newBlockForm.block_type,
        content_json: defaultContent,
      });
      
      toast.success('Block created successfully');
      setShowCreateBlock(false);
      setNewBlockForm({ block_key: '', block_type: 'content', content_json: {} });
      loadPageDetails(selectedPage.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create block');
    } finally {
      setCreatingBlock(false);
    }
  };
  
  const deleteBlock = async (blockId, blockKey) => {
    if (!window.confirm(`Delete the "${blockKey}" block? This cannot be undone.`)) {
      return;
    }
    
    try {
      await cmsApi.deleteBlock(blockId);
      toast.success('Block deleted');
      loadPageDetails(selectedPage.id);
    } catch (error) {
      toast.error('Failed to delete block');
    }
  };

  const saveBlock = async (blockId, contentJson) => {
    try {
      await cmsApi.updateBlock(blockId, { content_json: contentJson });
      toast.success('Block saved');
      loadPageDetails(selectedPage.id);
      setEditingBlock(null);
    } catch (error) {
      toast.error('Failed to save block');
    }
  };

  const toggleBlockEnabled = async (blockId, enabled) => {
    try {
      await cmsApi.updateBlock(blockId, { is_enabled: enabled });
      toast.success(enabled ? 'Block enabled' : 'Block disabled');
      loadPageDetails(selectedPage.id);
    } catch (error) {
      toast.error('Failed to update block');
    }
  };

  // ==================== NAVIGATION FUNCTIONS ====================
  const [editingNavItem, setEditingNavItem] = useState(null);
  const [navForm, setNavForm] = useState({ label: '', label_es: '', url: '', is_visible: true });

  const saveNavItem = async () => {
    if (!navForm.label || !navForm.url) {
      toast.error('Label and URL are required');
      return;
    }
    try {
      if (editingNavItem && editingNavItem !== 'new') {
        await cmsApi.updateNavItem(editingNavItem, navForm);
        toast.success('Navigation item updated');
      } else {
        await cmsApi.createNavItem({ ...navForm, order_index: navigation.length });
        toast.success('Navigation item created');
      }
      setEditingNavItem(null);
      setNavForm({ label: '', label_es: '', url: '', is_visible: true });
      loadInitialData();
    } catch (error) {
      toast.error('Failed to save navigation item');
    }
  };

  const deleteNavItem = async (navId) => {
    if (!window.confirm('Delete this navigation item?')) return;
    try {
      await cmsApi.deleteNavItem(navId);
      toast.success('Navigation item deleted');
      loadInitialData();
    } catch (error) {
      toast.error('Failed to delete navigation item');
    }
  };

  // ==================== MEDIA FUNCTIONS ====================
  const [uploading, setUploading] = useState(false);

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      await cmsApi.uploadMedia(file);
      toast.success('File uploaded successfully');
      loadInitialData();
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const deleteMedia = async (mediaId) => {
    if (!window.confirm('Delete this media file?')) return;
    try {
      await cmsApi.deleteMedia(mediaId);
      toast.success('Media deleted');
      loadInitialData();
    } catch (error) {
      toast.error('Failed to delete media');
    }
  };

  const copyMediaUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard');
  };

  // ==================== RENDER ====================
  return (
    <div className="space-y-6" data-testid="cms-manager">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-serif text-[#222222]">Content Management System</h2>
        <p className="text-sm text-[#555555]">Manage your website content, pages, and media</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 flex-wrap border-b border-[#E8E4DC] pb-2">
        {CMS_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setSelectedPage(null); }}
            className={`flex items-center gap-2 px-4 py-2 text-sm rounded-t transition-colors ${
              activeTab === tab.id
                ? 'bg-[#1F2A44] text-white'
                : 'text-[#555555] hover:bg-[#F5F3EF]'
            }`}
            data-testid={`cms-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F2A44]"></div>
        </div>
      ) : (
        <>
          {/* Content Migration Tab */}
          {activeTab === 'migration' && (
            <div className="space-y-6" data-testid="migration-section">
              <div className="bg-white border border-[#E8E4DC] p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#F5F3EF] rounded-lg">
                    <Database className="w-8 h-8 text-[#1F2A44]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-[#222222] mb-2">Content Migration Tool</h3>
                    <p className="text-[#555555] mb-4">
                      This tool will automatically migrate your existing website content into the CMS database. 
                      This enables you to edit content through the admin panel instead of code.
                    </p>
                    
                    {/* Migration Status */}
                    {migrationStatus === 'completed' && (
                      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded mb-4">
                        <Check className="w-5 h-5 text-green-600" />
                        <span className="text-green-800">Content has been migrated successfully! {pages.length} pages in database.</span>
                      </div>
                    )}
                    
                    {migrationStatus === 'failed' && (
                      <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded mb-4">
                        <AlertCircle className="w-5 h-5 text-red-600" />
                        <span className="text-red-800">Migration failed. Please try again.</span>
                      </div>
                    )}

                    <div className="bg-[#F5F3EF] p-4 rounded mb-4">
                      <h4 className="font-medium text-[#222222] mb-2">What gets migrated:</h4>
                      <ul className="text-sm text-[#555555] space-y-1 list-disc list-inside">
                        <li>Homepage content blocks (hero, services, features, CTA)</li>
                        <li>About page sections</li>
                        <li>Services page content</li>
                        <li>Pricing page information</li>
                        <li>Navigation menu items</li>
                        <li>Trademarks page content</li>
                      </ul>
                    </div>

                    <Button
                      onClick={runContentMigration}
                      disabled={migrating}
                      className="bg-[#1F2A44] hover:bg-[#151D30] text-white"
                      data-testid="run-migration-btn"
                    >
                      {migrating ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Migrating Content...
                        </>
                      ) : (
                        <>
                          <Database className="w-4 h-4 mr-2" />
                          {migrationStatus === 'completed' ? 'Re-run Migration' : 'Start Content Migration'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Migration Info Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border border-[#E8E4DC] p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <FileText className="w-5 h-5 text-[#C2A46D]" />
                    <span className="font-medium text-[#222222]">Pages</span>
                  </div>
                  <p className="text-2xl font-serif text-[#1F2A44]">{pages.length}</p>
                  <p className="text-xs text-[#555555]">in database</p>
                </div>
                <div className="bg-white border border-[#E8E4DC] p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Navigation className="w-5 h-5 text-[#C2A46D]" />
                    <span className="font-medium text-[#222222]">Navigation Items</span>
                  </div>
                  <p className="text-2xl font-serif text-[#1F2A44]">{navigation.length}</p>
                  <p className="text-xs text-[#555555]">in database</p>
                </div>
                <div className="bg-white border border-[#E8E4DC] p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Image className="w-5 h-5 text-[#C2A46D]" />
                    <span className="font-medium text-[#222222]">Media Files</span>
                  </div>
                  <p className="text-2xl font-serif text-[#1F2A44]">{media.length}</p>
                  <p className="text-xs text-[#555555]">uploaded</p>
                </div>
              </div>
            </div>
          )}

          {/* Pages Tab */}
          {activeTab === 'pages' && (
            <div className="space-y-6" data-testid="pages-section">
              {!selectedPage ? (
                // Pages List
                <div className="bg-white border border-[#E8E4DC]">
                  <div className="p-4 border-b border-[#E8E4DC] bg-[#F5F3EF] flex items-center justify-between">
                    <h3 className="font-semibold text-[#222222]">All Pages</h3>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-[#1F2A44] text-white">{pages.length} pages</Badge>
                      <Button
                        onClick={() => setShowCreatePage(true)}
                        className="bg-[#C2A46D] hover:bg-[#A8894D] text-white"
                        data-testid="create-page-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Page
                      </Button>
                    </div>
                  </div>
                  
                  {/* Create Page Modal */}
                  {showCreatePage && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-[#222222]">Create New Page</h3>
                          <button onClick={() => setShowCreatePage(false)} className="text-[#555555] hover:text-[#222222]">
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <Label>Page Title (English) *</Label>
                            <Input
                              value={newPageForm.title}
                              onChange={(e) => setNewPageForm({ ...newPageForm, title: e.target.value })}
                              placeholder="e.g., Privacy Policy"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label>Page Title (Spanish)</Label>
                            <Input
                              value={newPageForm.title_es}
                              onChange={(e) => setNewPageForm({ ...newPageForm, title_es: e.target.value })}
                              placeholder="e.g., Política de Privacidad"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label>URL Slug *</Label>
                            <div className="flex items-center mt-1">
                              <span className="text-sm text-[#555555] mr-1">/</span>
                              <Input
                                value={newPageForm.slug}
                                onChange={(e) => setNewPageForm({ ...newPageForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                                placeholder="privacy-policy"
                              />
                            </div>
                            <p className="text-xs text-[#777777] mt-1">Lowercase letters, numbers, and hyphens only</p>
                          </div>
                          <div>
                            <Label>Page Template</Label>
                            <Select
                              value={newPageForm.template}
                              onValueChange={(value) => setNewPageForm({ ...newPageForm, template: value })}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="default">Default (Full Width)</SelectItem>
                                <SelectItem value="content">Content Page</SelectItem>
                                <SelectItem value="landing">Landing Page</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex gap-2 pt-4">
                            <Button
                              variant="outline"
                              onClick={() => setShowCreatePage(false)}
                              className="flex-1"
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={createPage}
                              disabled={creatingPage}
                              className="flex-1 bg-[#1F2A44] hover:bg-[#151D30] text-white"
                            >
                              {creatingPage ? 'Creating...' : 'Create Page'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {pages.length === 0 ? (
                    <div className="p-8 text-center">
                      <FileText className="w-12 h-12 text-[#E8E4DC] mx-auto mb-4" />
                      <p className="text-[#555555] mb-2">No pages in the CMS yet</p>
                      <p className="text-sm text-[#777777] mb-4">Run the content migration tool to import your existing pages</p>
                      <Button
                        onClick={() => setActiveTab('migration')}
                        className="bg-[#C2A46D] hover:bg-[#A8894D] text-white"
                      >
                        <Database className="w-4 h-4 mr-2" />
                        Go to Migration Tool
                      </Button>
                    </div>
                  ) : (
                    <div className="divide-y divide-[#E8E4DC]">
                      {pages.map((page) => (
                        <div 
                          key={page.id}
                          className="p-4 flex items-center justify-between hover:bg-[#FDFCFA] cursor-pointer"
                          onClick={() => loadPageDetails(page.id)}
                          data-testid={`page-${page.slug}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-[#F5F3EF] rounded flex items-center justify-center">
                              <FileText className="w-5 h-5 text-[#1F2A44]" />
                            </div>
                            <div>
                              <p className="font-medium text-[#222222]">{page.title}</p>
                              <p className="text-sm text-[#555555]">/{page.slug}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={page.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                              {page.status}
                            </Badge>
                            <ChevronRight className="w-5 h-5 text-[#555555]" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                // Page Editor
                <div className="space-y-6">
                  {/* Page Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Button
                        variant="ghost"
                        onClick={() => setSelectedPage(null)}
                        className="text-[#555555]"
                      >
                        <ChevronRight className="w-4 h-4 rotate-180 mr-2" />
                        Back to Pages
                      </Button>
                      <div>
                        <h3 className="text-xl font-serif text-[#222222]">{selectedPage.title}</h3>
                        <p className="text-sm text-[#555555]">/{selectedPage.slug}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={selectedPage.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {selectedPage.status}
                      </Badge>
                      <Button
                        variant="outline"
                        onClick={() => {
                          const slug = selectedPage.slug === 'home' ? '' : selectedPage.slug;
                          window.open(`/${slug}`, '_blank');
                        }}
                        className="border-[#555555] text-[#555555]"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Preview
                      </Button>
                      {!["home", "about", "services", "pricing", "contact", "resources"].includes(selectedPage.slug) && (
                        <Button
                          variant="outline"
                          onClick={() => deletePage(selectedPage.id, selectedPage.slug)}
                          className="border-red-500 text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      )}
                      <Button
                        onClick={() => publishPage(selectedPage.id)}
                        className="bg-[#1F2A44] hover:bg-[#151D30] text-white"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Publish Page
                      </Button>
                    </div>
                  </div>

                  {/* SEO Settings */}
                  <div className="bg-white border border-[#E8E4DC]">
                    <button 
                      className="w-full p-4 border-b border-[#E8E4DC] bg-[#F5F3EF] flex items-center justify-between"
                      onClick={() => setShowSeoSettings(!showSeoSettings)}
                    >
                      <div className="flex items-center gap-2">
                        <LinkIcon className="w-4 h-4 text-[#555555]" />
                        <h4 className="font-semibold text-[#222222]">SEO Settings</h4>
                        {(selectedPage.seo_title || selectedPage.og_title) && (
                          <Badge className="bg-green-100 text-green-800 text-xs">Configured</Badge>
                        )}
                      </div>
                      {showSeoSettings ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    </button>
                    
                    {showSeoSettings && (
                      <div className="p-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="seo_title">Meta Title</Label>
                            <Input
                              id="seo_title"
                              value={seoForm.seo_title}
                              onChange={(e) => setSeoForm(prev => ({ ...prev, seo_title: e.target.value }))}
                              placeholder={selectedPage.title}
                              className="mt-1"
                            />
                            <p className="text-xs text-[#555555] mt-1">Appears in browser tab and search results</p>
                          </div>
                          <div>
                            <Label htmlFor="og_title">Open Graph Title</Label>
                            <Input
                              id="og_title"
                              value={seoForm.og_title}
                              onChange={(e) => setSeoForm(prev => ({ ...prev, og_title: e.target.value }))}
                              placeholder={seoForm.seo_title || selectedPage.title}
                              className="mt-1"
                            />
                            <p className="text-xs text-[#555555] mt-1">Title when shared on social media</p>
                          </div>
                        </div>
                        
                        <div>
                          <Label htmlFor="seo_description">Meta Description</Label>
                          <Textarea
                            id="seo_description"
                            value={seoForm.seo_description}
                            onChange={(e) => setSeoForm(prev => ({ ...prev, seo_description: e.target.value }))}
                            placeholder="Enter a brief description of this page for search engines..."
                            className="mt-1"
                            rows={2}
                          />
                          <p className="text-xs text-[#555555] mt-1">{seoForm.seo_description.length}/160 characters (recommended)</p>
                        </div>
                        
                        <div>
                          <Label htmlFor="og_description">Open Graph Description</Label>
                          <Textarea
                            id="og_description"
                            value={seoForm.og_description}
                            onChange={(e) => setSeoForm(prev => ({ ...prev, og_description: e.target.value }))}
                            placeholder={seoForm.seo_description || "Description when shared on social media..."}
                            className="mt-1"
                            rows={2}
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="og_image">Open Graph Image URL</Label>
                          <Input
                            id="og_image"
                            value={seoForm.og_image}
                            onChange={(e) => setSeoForm(prev => ({ ...prev, og_image: e.target.value }))}
                            placeholder="https://example.com/image.jpg"
                            className="mt-1"
                          />
                          <p className="text-xs text-[#555555] mt-1">Image shown when shared on social media (1200x630px recommended)</p>
                        </div>
                        
                        <div className="flex justify-end pt-2">
                          <Button 
                            onClick={saveSeoSettings} 
                            disabled={savingSeo}
                            className="bg-[#1F2A44]"
                          >
                            {savingSeo ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <Save className="w-4 h-4 mr-2" />
                                Save SEO Settings
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Content Blocks */}
                  <div className="bg-white border border-[#E8E4DC]">
                    <div className="p-4 border-b border-[#E8E4DC] bg-[#F5F3EF] flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-[#222222]">Content Blocks</h4>
                        <p className="text-sm text-[#555555]">Edit the content sections on this page</p>
                      </div>
                      <Button
                        onClick={() => setShowCreateBlock(true)}
                        className="bg-[#C2A46D] hover:bg-[#A8894D] text-white"
                        size="sm"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Block
                      </Button>
                    </div>
                    
                    {/* Create Block Modal */}
                    {showCreateBlock && (
                      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-[#222222]">Add New Block</h3>
                            <button onClick={() => setShowCreateBlock(false)} className="text-[#555555] hover:text-[#222222]">
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                          <div className="space-y-4">
                            <div>
                              <Label>Block Key *</Label>
                              <Input
                                value={newBlockForm.block_key}
                                onChange={(e) => setNewBlockForm({ ...newBlockForm, block_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                                placeholder="e.g., hero, features, cta_section"
                                className="mt-1"
                              />
                              <p className="text-xs text-[#777777] mt-1">Unique identifier for this block (lowercase, underscores)</p>
                            </div>
                            <div>
                              <Label>Block Type *</Label>
                              <Select
                                value={newBlockForm.block_type}
                                onValueChange={(value) => setNewBlockForm({ ...newBlockForm, block_type: value })}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="hero">Hero Section</SelectItem>
                                  <SelectItem value="content">Content Section</SelectItem>
                                  <SelectItem value="features">Features Grid</SelectItem>
                                  <SelectItem value="cta">Call to Action</SelectItem>
                                  <SelectItem value="testimonials">Testimonials</SelectItem>
                                  <SelectItem value="gallery">Image Gallery</SelectItem>
                                  <SelectItem value="faq">FAQ Section</SelectItem>
                                  <SelectItem value="contact">Contact Info</SelectItem>
                                  <SelectItem value="custom">Custom Block</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="bg-[#F5F3EF] p-3 rounded text-sm text-[#555555]">
                              <strong>Tip:</strong> After creating the block, you can edit its content and add fields as needed.
                            </div>
                            <div className="flex gap-2 pt-4">
                              <Button
                                variant="outline"
                                onClick={() => setShowCreateBlock(false)}
                                className="flex-1"
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={createBlock}
                                disabled={creatingBlock}
                                className="flex-1 bg-[#1F2A44] hover:bg-[#151D30] text-white"
                              >
                                {creatingBlock ? 'Creating...' : 'Add Block'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="divide-y divide-[#E8E4DC]">
                      {pageBlocks.map((block) => (
                        <div key={block.id} className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <Badge className="bg-[#F5F3EF] text-[#555555]">{block.block_type}</Badge>
                              <span className="font-medium text-[#222222]">{block.block_key}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={block.is_enabled}
                                onCheckedChange={(checked) => toggleBlockEnabled(block.id, checked)}
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setEditingBlock(editingBlock === block.id ? null : block.id)}
                                className="text-[#1F2A44] border-[#1F2A44]"
                              >
                                {editingBlock === block.id ? <ChevronDown className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => deleteBlock(block.id, block.block_key)}
                                className="text-red-500 border-red-300 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          {editingBlock === block.id && (
                            <BlockEditor
                              block={block}
                              onSave={(content) => saveBlock(block.id, content)}
                              onCancel={() => setEditingBlock(null)}
                            />
                          )}
                        </div>
                      ))}
                      
                      {pageBlocks.length === 0 && (
                        <div className="p-8 text-center text-[#555555]">
                          <p className="mb-2">No content blocks found for this page</p>
                          <Button
                            onClick={() => setShowCreateBlock(true)}
                            variant="outline"
                            className="text-[#1F2A44] border-[#1F2A44]"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Your First Block
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Version History */}
                  {pageVersions.length > 0 && (
                    <div className="bg-white border border-[#E8E4DC]">
                      <div className="p-4 border-b border-[#E8E4DC] bg-[#F5F3EF] flex items-center gap-2">
                        <History className="w-4 h-4 text-[#555555]" />
                        <h4 className="font-semibold text-[#222222]">Version History</h4>
                      </div>
                      <div className="divide-y divide-[#E8E4DC]">
                        {pageVersions.slice(0, 5).map((version) => (
                          <div key={version.id} className="p-4 flex items-center justify-between">
                            <div>
                              <p className="font-medium text-[#222222]">Version {version.version_number}</p>
                              <p className="text-sm text-[#555555]">
                                {new Date(version.created_at).toLocaleDateString()} by {version.created_by}
                              </p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => restoreVersion(version.id)}
                              className="text-[#555555]"
                            >
                              Restore
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Navigation Tab */}
          {activeTab === 'navigation' && (
            <div className="space-y-6" data-testid="navigation-section">
              <div className="bg-white border border-[#E8E4DC]">
                <div className="p-4 border-b border-[#E8E4DC] bg-[#F5F3EF] flex items-center justify-between">
                  <h3 className="font-semibold text-[#222222]">Navigation Menu</h3>
                  <Button
                    onClick={() => { setEditingNavItem('new'); setNavForm({ label: '', label_es: '', url: '', is_visible: true }); }}
                    className="bg-[#1F2A44] hover:bg-[#151D30] text-white"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                </div>

                {/* Nav Item Form */}
                {editingNavItem && (
                  <div className="p-4 border-b border-[#E8E4DC] bg-[#FDFCFA]">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label>Label (English) *</Label>
                        <Input
                          value={navForm.label}
                          onChange={(e) => setNavForm({ ...navForm, label: e.target.value })}
                          placeholder="Home"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Label (Spanish)</Label>
                        <Input
                          value={navForm.label_es}
                          onChange={(e) => setNavForm({ ...navForm, label_es: e.target.value })}
                          placeholder="Inicio"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label>URL *</Label>
                        <Input
                          value={navForm.url}
                          onChange={(e) => setNavForm({ ...navForm, url: e.target.value })}
                          placeholder="/about"
                          className="mt-1"
                        />
                      </div>
                      <div className="flex items-center gap-2 pt-6">
                        <Switch
                          checked={navForm.is_visible}
                          onCheckedChange={(checked) => setNavForm({ ...navForm, is_visible: checked })}
                        />
                        <Label>Visible</Label>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={saveNavItem} className="bg-[#1F2A44] text-white">
                        <Save className="w-4 h-4 mr-2" />
                        Save
                      </Button>
                      <Button variant="outline" onClick={() => setEditingNavItem(null)}>Cancel</Button>
                    </div>
                  </div>
                )}

                {/* Nav Items List */}
                <div className="divide-y divide-[#E8E4DC]">
                  {navigation.map((item, index) => (
                    <div key={item.id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="w-6 h-6 bg-[#F5F3EF] rounded flex items-center justify-center text-sm text-[#555555]">
                          {index + 1}
                        </span>
                        <div>
                          <p className="font-medium text-[#222222]">{item.label}</p>
                          <p className="text-sm text-[#555555]">{item.url}</p>
                        </div>
                        {!item.is_visible && (
                          <Badge className="bg-gray-100 text-gray-600">Hidden</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingNavItem(item.id);
                            setNavForm({
                              label: item.label,
                              label_es: item.label_es || '',
                              url: item.url,
                              is_visible: item.is_visible
                            });
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteNavItem(item.id)}
                          className="text-red-600 border-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {navigation.length === 0 && (
                    <div className="p-8 text-center">
                      <Navigation className="w-12 h-12 text-[#E8E4DC] mx-auto mb-4" />
                      <p className="text-[#555555]">No navigation items yet</p>
                      <p className="text-sm text-[#777777]">Run the migration tool or add items manually</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Forms Tab */}
          {activeTab === 'forms' && (
            <FormBuilder />
          )}

          {/* Media Library Tab */}
          {activeTab === 'media' && (
            <div className="space-y-6" data-testid="media-section">
              <div className="bg-white border border-[#E8E4DC]">
                <div className="p-4 border-b border-[#E8E4DC] bg-[#F5F3EF] flex items-center justify-between">
                  <h3 className="font-semibold text-[#222222]">Media Library</h3>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx"
                      onChange={handleMediaUpload}
                      disabled={uploading}
                    />
                    <Button
                      asChild
                      disabled={uploading}
                      className="bg-[#1F2A44] hover:bg-[#151D30] text-white"
                    >
                      <span>
                        {uploading ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Upload File
                      </span>
                    </Button>
                  </label>
                </div>

                {media.length === 0 ? (
                  <div className="p-8 text-center">
                    <Image className="w-12 h-12 text-[#E8E4DC] mx-auto mb-4" />
                    <p className="text-[#555555]">No media files uploaded yet</p>
                    <p className="text-sm text-[#777777]">Upload images and documents for your website</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 p-4">
                    {media.map((item) => (
                      <div key={item.id} className="border border-[#E8E4DC] rounded overflow-hidden group">
                        {item.file_type === 'image' ? (
                          <div className="aspect-square bg-[#F5F3EF] relative">
                            <img
                              src={item.url}
                              alt={item.alt_text || item.original_filename}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => copyMediaUrl(item.url)}
                              >
                                <LinkIcon className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => deleteMedia(item.id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="aspect-square bg-[#F5F3EF] flex items-center justify-center">
                            <FileText className="w-8 h-8 text-[#555555]" />
                          </div>
                        )}
                        <div className="p-2">
                          <p className="text-xs text-[#555555] truncate">{item.original_filename}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Audit Log Tab */}
          {activeTab === 'audit' && (
            <AuditLogViewer />
          )}
        </>
      )}
    </div>
  );
};

// ==================== BLOCK EDITOR COMPONENT ====================
const BlockEditor = ({ block, onSave, onCancel, onSaveDraft }) => {
  const [content, setContent] = useState(block.content_json || {});
  const [activeTab, setActiveTab] = useState('english');
  const [showAddField, setShowAddField] = useState(false);
  const [newField, setNewField] = useState({ key: '', type: 'text', bilingual: true });

  const handleFieldChange = (key, value) => {
    setContent(prev => ({ ...prev, [key]: value }));
  };
  
  const addField = () => {
    if (!newField.key) {
      return;
    }
    const fieldKey = newField.key.toLowerCase().replace(/[^a-z0-9]/g, '_');
    
    let defaultValue = '';
    if (newField.type === 'array') defaultValue = [];
    if (newField.type === 'boolean') defaultValue = false;
    if (newField.type === 'number') defaultValue = 0;
    
    const updatedContent = { ...content, [fieldKey]: defaultValue };
    
    // Add Spanish version if bilingual
    if (newField.bilingual && newField.type === 'text') {
      updatedContent[`${fieldKey}_es`] = '';
    }
    
    setContent(updatedContent);
    setNewField({ key: '', type: 'text', bilingual: true });
    setShowAddField(false);
  };
  
  const removeField = (key) => {
    const updated = { ...content };
    delete updated[key];
    // Also remove Spanish version if exists
    if (updated[`${key}_es`]) {
      delete updated[`${key}_es`];
    }
    setContent(updated);
  };

  // Group fields by language (English and Spanish)
  const groupFieldsByLanguage = () => {
    const english = [];
    const spanish = [];
    
    Object.entries(block.content_json || {}).forEach(([key, value]) => {
      if (key.endsWith('_es')) {
        spanish.push({ key, value, baseKey: key.replace('_es', '') });
      } else if (!Object.keys(block.content_json).includes(`${key}_es`)) {
        english.push({ key, value, isShared: true });
      } else {
        english.push({ key, value, isShared: false });
      }
    });
    
    return { english, spanish };
  };

  const renderField = (key, value, label = null) => {
    const displayLabel = label || key.replace(/_es$/, '').replace(/_/g, ' ');
    
    // Check if this is an image field
    const isImageField = key.includes('image') || key.includes('img') || key.includes('photo') || key.includes('logo') || key.includes('src');
    
    if (typeof value === 'string') {
      const isLongText = value.length > 100 || key.includes('content') || key.includes('description') || key.includes('paragraph') || key.includes('subheadline');
      
      // Image field rendering
      if (isImageField) {
        return (
          <div key={key} className="mb-4 col-span-2">
            <Label className="text-xs font-medium text-[#555555] capitalize flex items-center gap-2">
              {displayLabel}
              <Badge className="bg-purple-100 text-purple-800 text-[10px]">Image</Badge>
            </Label>
            <div className="mt-1 space-y-2">
              {content[key] && (
                <div className="relative inline-block">
                  <img 
                    src={content[key]} 
                    alt={displayLabel}
                    className="h-24 w-auto object-cover rounded border border-[#E8E4DC]"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
              <Input
                value={content[key] || ''}
                onChange={(e) => handleFieldChange(key, e.target.value)}
                className="text-sm border-[#E8E4DC] focus:border-[#C2A46D] focus:ring-[#C2A46D]"
                placeholder="Enter image URL or upload to Media Library..."
              />
              <p className="text-xs text-[#777777]">Enter a URL or upload an image in the Media Library tab and paste the URL here.</p>
            </div>
          </div>
        );
      }
      
      return (
        <div key={key} className="mb-4">
          <Label className="text-xs font-medium text-[#555555] capitalize flex items-center gap-2">
            {displayLabel}
            {key.endsWith('_es') && <Badge className="bg-orange-100 text-orange-800 text-[10px]">ES</Badge>}
          </Label>
          {isLongText ? (
            <Textarea
              value={content[key] || ''}
              onChange={(e) => handleFieldChange(key, e.target.value)}
              className="mt-1 text-sm border-[#E8E4DC] focus:border-[#C2A46D] focus:ring-[#C2A46D]"
              rows={3}
              placeholder={`Enter ${displayLabel}...`}
            />
          ) : (
            <Input
              value={content[key] || ''}
              onChange={(e) => handleFieldChange(key, e.target.value)}
              className="mt-1 text-sm border-[#E8E4DC] focus:border-[#C2A46D] focus:ring-[#C2A46D]"
              placeholder={`Enter ${displayLabel}...`}
            />
          )}
        </div>
      );
    }
    
    if (Array.isArray(value)) {
      return (
        <div key={key} className="mb-4 col-span-2">
          <Label className="text-xs font-medium text-[#555555] capitalize flex items-center gap-2">
            {displayLabel}
            <Badge className="bg-blue-100 text-blue-800 text-[10px]">Array</Badge>
          </Label>
          <Textarea
            value={JSON.stringify(content[key] || [], null, 2)}
            onChange={(e) => {
              try {
                handleFieldChange(key, JSON.parse(e.target.value));
              } catch {}
            }}
            className="mt-1 text-sm font-mono border-[#E8E4DC] focus:border-[#C2A46D]"
            rows={6}
            placeholder="Edit as JSON array..."
          />
          <p className="text-xs text-[#777777] mt-1">Edit as JSON. Changes apply when valid JSON is entered.</p>
        </div>
      );
    }
    
    return null;
  };

  // Organize fields for better UX
  const organizeFields = () => {
    const entries = Object.entries(block.content_json || {});
    const englishFields = [];
    const spanishFields = [];
    const sharedFields = [];
    
    entries.forEach(([key, value]) => {
      if (key.endsWith('_es')) {
        spanishFields.push({ key, value });
      } else {
        const hasSpanish = entries.some(([k]) => k === `${key}_es`);
        if (hasSpanish) {
          englishFields.push({ key, value });
        } else {
          sharedFields.push({ key, value });
        }
      }
    });
    
    return { englishFields, spanishFields, sharedFields };
  };

  const { englishFields, spanishFields, sharedFields } = organizeFields();
  const hasSpanishContent = spanishFields.length > 0;

  return (
    <div className="mt-4 p-4 bg-[#F5F3EF] rounded border border-[#E8E4DC]">
      {/* Block Type Info */}
      <div className="mb-4 pb-3 border-b border-[#E8E4DC]">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs text-[#777777]">Block Type:</span>
            <Badge className="ml-2 bg-[#1F2A44] text-white">{block.block_type}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-xs text-[#777777]">
              Key: <code className="bg-white px-1 py-0.5 rounded">{block.block_key}</code>
            </div>
            <Button
              onClick={() => setShowAddField(true)}
              variant="outline"
              size="sm"
              className="text-[#C2A46D] border-[#C2A46D] hover:bg-[#C2A46D] hover:text-white"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Field
            </Button>
          </div>
        </div>
      </div>
      
      {/* Add Field Modal */}
      {showAddField && (
        <div className="mb-4 p-3 bg-white rounded border border-[#C2A46D]">
          <div className="flex items-center justify-between mb-3">
            <h5 className="font-medium text-sm text-[#222222]">Add New Field</h5>
            <button onClick={() => setShowAddField(false)} className="text-[#555555] hover:text-[#222222]">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Field Name</Label>
              <Input
                value={newField.key}
                onChange={(e) => setNewField({ ...newField, key: e.target.value })}
                placeholder="e.g., subtitle"
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Field Type</Label>
              <Select value={newField.type} onValueChange={(v) => setNewField({ ...newField, type: v })}>
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="textarea">Long Text</SelectItem>
                  <SelectItem value="image">Image URL</SelectItem>
                  <SelectItem value="url">URL/Link</SelectItem>
                  <SelectItem value="array">List/Array</SelectItem>
                  <SelectItem value="boolean">Yes/No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  checked={newField.bilingual}
                  onChange={(e) => setNewField({ ...newField, bilingual: e.target.checked })}
                  className="rounded"
                />
                Bilingual
              </label>
              <Button onClick={addField} size="sm" className="bg-[#1F2A44] text-white">
                Add
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Language Tabs (if bilingual content exists) */}
      {hasSpanishContent && (
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveTab('english')}
            className={`px-3 py-1.5 text-sm rounded transition-colors ${
              activeTab === 'english' 
                ? 'bg-[#1F2A44] text-white' 
                : 'bg-white text-[#555555] border border-[#E8E4DC] hover:border-[#1F2A44]'
            }`}
          >
            English Content
          </button>
          <button
            onClick={() => setActiveTab('spanish')}
            className={`px-3 py-1.5 text-sm rounded transition-colors ${
              activeTab === 'spanish' 
                ? 'bg-[#C2A46D] text-white' 
                : 'bg-white text-[#555555] border border-[#E8E4DC] hover:border-[#C2A46D]'
            }`}
          >
            Spanish Content
          </button>
        </div>
      )}

      {/* Shared Fields (non-translatable) */}
      {sharedFields.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {sharedFields.map(({ key, value }) => renderField(key, value))}
        </div>
      )}

      {/* English Fields */}
      {(!hasSpanishContent || activeTab === 'english') && englishFields.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {englishFields.map(({ key, value }) => renderField(key, value))}
        </div>
      )}

      {/* Spanish Fields */}
      {hasSpanishContent && activeTab === 'spanish' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {spanishFields.map(({ key, value }) => renderField(key, value, key.replace('_es', '')))}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 mt-6 pt-4 border-t border-[#E8E4DC]">
        <Button onClick={() => onSave(content)} className="bg-[#1F2A44] hover:bg-[#151D30] text-white" size="sm">
          <Save className="w-4 h-4 mr-2" />
          Save & Publish
        </Button>
        {onSaveDraft && (
          <Button onClick={() => onSaveDraft(content)} variant="outline" size="sm" className="border-[#C2A46D] text-[#C2A46D] hover:bg-[#C2A46D] hover:text-white">
            <Clock className="w-4 h-4 mr-2" />
            Save as Draft
          </Button>
        )}
        <Button onClick={onCancel} variant="outline" size="sm">Cancel</Button>
      </div>
    </div>
  );
};

export default CMSManager;
